// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartContext } from "@insite/client-framework/Components/CartContext";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { CartModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    allProperties,
    detailsPropertyFieldDefinitions,
} from "@store/Library/Widgets/DetailsPropertyDisplay/detailsPropertyDispayhelpers";
import usePageType from "@toolkit/Framework/Common/GoogleAnalytics/hooks/utilities/usePageType";
import get from "lodash/get";
import { useContext } from "react";
import React from "react";
import { css } from "styled-components";

enum fields {
    selectedProperty = "selectedProperty",
    customLabel = "customLabel",
    titleType = "titleType",
    titleStyle = "titleStyle",
    layout = "layout",
    gap = "gap",
    customProperty = "customProperty",
    customCSS = "customCSS",
    customPath = "customPath",
    customPathType = "customPathType",
    customPathOverride = "customPathOverride",
    defaultValue = "defaultValue",
}
type DataTypes = "boolean" | "string" | "number" | "date" | "custom";
export interface OrderPropertyDisplayStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    propertyText?: TypographyPresentationProps;
}

export const orderPropertyDisplayStyles: OrderPropertyDisplayStyles = {
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    titleText: {
        weight: "bold",
    },
};

const styles = orderPropertyDisplayStyles;

interface Props extends WidgetProps {
    fields: {
        [fields.selectedProperty]: keyof Partial<OrderModel> | keyof Partial<CartModel>;
        [fields.customLabel]: string;
        [fields.titleType]: keyof React.ReactHTML;
        [fields.titleStyle]: keyof ThemeTypography | undefined | "label";
        [fields.layout]: "row" | "column";
        [fields.gap]: string;
        [fields.customProperty]: string;
        [fields.customCSS]: string;
        [fields.customPath]: string;
        [fields.customPathType]?: "boolean" | "string" | "number" | "date";
        [fields.defaultValue]: string;
    };
}

const getData = (
    contextToUse: Partial<CartModel> | Partial<OrderModel> | undefined,
    customPath: string | undefined,
    customPathType: DataTypes | undefined,
    customProperty: string | undefined,
    selectedProperty: keyof Partial<CartModel> | keyof Partial<OrderModel>,
): { data: string | number | boolean | Date | undefined; dataType: DataTypes | undefined } => {
    let data: string | number | boolean | Date | undefined;
    let dataType: DataTypes | undefined = customPath ? customPathType : allProperties[selectedProperty];

    if (customPath) {
        const dataBeforeValidation = get(contextToUse, customPath);
        // this is a little tricky because we want to make sure we don't accidentally render an object in the UI as that would cause an error
        // but we still want to maintain the ability to render a date object as a date
        data = typeof dataBeforeValidation === "object" ? undefined : dataBeforeValidation;
        if (customPathType === "date" && dataBeforeValidation instanceof Date) {
            data = dataBeforeValidation;
        }
    } else if (dataType === "custom" && customProperty) {
        data = contextToUse?.properties?.[customProperty];
        dataType = "string";
    } else {
        data = contextToUse?.[selectedProperty as keyof typeof contextToUse] as
            | string
            | number
            | boolean
            | Date
            | undefined;
    }

    return { data, dataType };
};

const OrderPropertyDisplay: React.FC<Props> = ({
    fields: {
        customLabel,
        selectedProperty,
        titleType,
        titleStyle,
        layout,
        gap,
        customProperty,
        customCSS,
        customPath,
        customPathType,
        defaultValue,
    },
}) => {
    const { value: order } = useContext(OrderStateContext);
    const cart = useContext(CartContext);
    const pageName = usePageType();
    const contextToUse = pageName === "OrderDetailsPage" ? order : cart;

    const isPropertyValid = contextToUse && selectedProperty in contextToUse;
    const isCustomPathValid = !!customPath;

    if (!contextToUse || (!isPropertyValid && !isCustomPathValid)) {
        return null;
    }

    let { data, dataType } = getData(contextToUse, customPath, customPathType, customProperty, selectedProperty);

    if (data === undefined || data === null) {
        if (!defaultValue) {
            return null;
        }
        data = defaultValue;
        dataType = "string";
    }

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css}
            flex-direction: ${layout};
            gap: ${gap};
            align-items: ${layout === "row" ? "center" : "flex-start"};
            justify-content: ${layout === "row" ? "flex-start" : "center"};
            ${customCSS}
        `,
    };
    const titleTextStyles = {
        ...styles.titleText,
        css: css`
            ${styles.titleText?.css};
            margin-bottom: 0;
        `,
    };
    titleStyle = titleStyle === "label" ? undefined : titleStyle;

    return (
        <StyledWrapper {...wrapperStyles} className="wrapper">
            {customLabel && (
                <Typography as={titleType} {...titleTextStyles} variant={titleStyle} className="title">
                    {customLabel}
                </Typography>
            )}
            <StyledWrapper className="property">
                {dataType === "date" && data instanceof Date && (
                    <LocalizedDateTime
                        dateTime={data}
                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                    />
                )}
                {dataType === "number" && typeof data === "number" && <LocalizedCurrency amount={data} />}
                {dataType === "string" && (
                    <Typography {...styles.propertyText} className="property">
                        {data}
                    </Typography>
                )}
                {dataType === "boolean" && (
                    <Typography {...styles.propertyText} className="property">
                        {data ? "Yes" : "No"}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderPropertyDisplay,
    definition: {
        group: "Order Details",
        allowedContexts: ["OrderDetailsPage", "OrderApprovalDetailsPage", "SavedOrderDetailsPage"],
        fieldDefinitions: detailsPropertyFieldDefinitions,
    },
};

export default widgetModule;
